import { PlanType } from '@showbie-socrative/socrative-utils';

import { useAccount } from '~teacher/hook/account';
import { useGetWorkspaceRole } from '~teacher/hook/workspaces/useGetWorkspaceRole';

import { AccountResponse } from '../../../../api/account/types';
import { WorkspaceMember } from '../../../../api/workspaces/getWorkspaceMembers';
import { composePermissions } from './composePermissions';

interface UseWorkspacePermissionsParams {
  workspaceId: string;
}

function selectIsProAccount(accountResponse: AccountResponse) {
  return [PlanType.Pro, PlanType.Complete, PlanType.Organization].includes(
    accountResponse.plan
  );
}

function selectWorkspaceMemberRole(workspaceMember: WorkspaceMember) {
  return workspaceMember.role;
}

/**
 * Use this hook to determine the effective permissions that the current logged in member has in a given workspace
 * @param workspaceId Id of the
 *
 * @example
 * ```
 * const { permissions } = useWorkspacePermissions({
 *   workspaceId: '60269fc9-6b40-45ec-bbfd-f69711f0f4c7',
 * });
 * ```
 *
 */
export function useWorkspacePermissionsV2({
  workspaceId,
}: UseWorkspacePermissionsParams) {
  const accountQuery = useAccount({
    select: selectIsProAccount,
  });

  const roleQuery = useGetWorkspaceRole({
    workspaceId,
    select: selectWorkspaceMemberRole,
  });

  const permissions = composePermissions({
    isBetaUser: false,
    isPro: accountQuery.data,
    workspaceMemberType: roleQuery.data,
  });

  return {
    workspaceRole: roleQuery.data,
    isLoading: [accountQuery, roleQuery].some((query) => query.isLoading),
    accountQuery,
    roleQuery,
    permissions,
  };
}
